import {
  ApolloQueryResult,
  OperationVariables,
  useMutation
} from '@apollo/client'
import { Button, Typography } from '@material-ui/core'
import RefreshIcon from '@material-ui/icons/Refresh'
import moment from 'moment'
import React, { useMemo } from 'react'
import { toast } from 'react-toastify'

import { addJob } from '../../cache/jobs'
import { INVENTORY_RETRY_FAILED_ALLOCATION } from '../../graphql/inventoryAllocateAvailability'
import { DateObject, getTodayDate } from '../../helpers/date'
import { getTranslate } from '../../localization'
import { JobsType } from '../../mockData/jobs'
import {
  AllocationLogDetails,
  DailyAllocationDetail
} from '../../types/inventoryAllocationLogs'
import useStyles from './styles/InventoryRoomtypeBox.styles'

interface TotalAllocateRowProps {
  dailyLogAllocations: DailyAllocationDetail[]
  selectedDateInDatePicker: DateObject
  requestID: string
  canRetry: boolean
  refetch: (variables?: Partial<OperationVariables> | undefined) => Promise<
    ApolloQueryResult<{
      allocationLogDetails: AllocationLogDetails
    }>
  >
  status: string
}

const TotalAllocateRow: React.FC<TotalAllocateRowProps> = ({
  dailyLogAllocations,
  selectedDateInDatePicker,
  canRetry,
  requestID,
  refetch,
  status
}) => {
  const classes = useStyles()

  // Helper to filter the dailyLogAllocations based on the selected date range
  const filteredAllocations = useMemo(() => {
    const startDate = moment(selectedDateInDatePicker.gregorianStartDate)
    const endDate = moment(selectedDateInDatePicker.gregorianEndDate)

    return dailyLogAllocations.filter((allocation) => {
      const allocationDate = moment(allocation.date)
      return allocationDate.isBetween(startDate, endDate, 'day', '[]') // Inclusive range
    })
  }, [dailyLogAllocations, selectedDateInDatePicker])

  const [mutateFunction, { loading }] = useMutation(
    INVENTORY_RETRY_FAILED_ALLOCATION,
    {
      onCompleted: (response) => {
        if (response.retryFailedAllocation.trackingID) {
          const newJob: JobsType = {
            trackID: response.retryFailedAllocation.trackingID,
            creationDate: getTodayDate(),
            status: 'InProgress'
          }
          addJob(newJob)
          toast.info(
            getTranslate(
              `retry retryFailedAllocation job with tracking id ${response.retryFailedAllocation.trackingID} started`
            )
          )
          refetch()
        } else {
          throw new Error(getTranslate('Server error, please try again'))
        }
      },
      onError: (err) => toast.error(err.message)
    }
  )

  const onSave = () => {
    mutateFunction({
      variables: {
        requestID: requestID
      }
    })
  }
  return (
    <div>
      <div className={classes.totalRoomTypeRow}>
        <div>
          <div className={classes.rowNameContainer}>
            <Typography variant="h3" className={classes.totalAllocateTitle}>
              {getTranslate('Total Allocate')}
            </Typography>{' '}
            <Button
              variant="text"
              color="primary"
              startIcon={<RefreshIcon />}
              disabled={
                !canRetry ||
                loading ||
                status === 'InProgress' ||
                status === 'Retrying'
              }
              onClick={onSave}>
              {getTranslate('Retry')}
            </Button>
          </div>
        </div>

        {filteredAllocations?.map((allocation) => (
          <div
            key={`${allocation.date}`}
            className={`${classes.dayContainer} ${
              allocation.successful < allocation.requested
                ? classes.totalFailedBackground // Failed allocation
                : classes.totalSuccessBackground // Successful allocation
            } ${classes.totalAllocationContainer}`}>
            <Typography variant="body2" className={classes.totalSuccessText}>
              {allocation.successful + ' '}
            </Typography>
            <Typography variant="body2" className={classes.totalNumberOfRooms}>
              {` /${allocation.requested}`}
            </Typography>
          </div>
        ))}
      </div>
    </div>
  )
}

export default TotalAllocateRow
